import type { CatalogSong } from './catalog.ts'
import { normaliseCatalogTitle, recordingKind, type RecordingKind } from './catalog-match.ts'

/**
 * The shared song catalog (docs/15-MUSIC-AND-AI.md, "The shared catalog"):
 * which recordings are Christian or gospel, how suggestions are ranked while
 * someone types a song's title, how a recording pre-fills a new song, and
 * how many churches sing it — shown only when enough do that no church can
 * be picked out.
 */

// ---- Christian and gospel ------------------------------------------------------------------

/**
 * A genre that is Christian or gospel music. Apple's genres are words, not
 * ids, and differ by storefront: Christian, Gospel, Christian & Gospel,
 * Praise & Worship, Worship, CCM, Inspirational, Southern Gospel,
 * Contemporary Gospel, Traditional Gospel, Christian Rock / Pop / Rap /
 * Metal / Hip-Hop… and the same in a few other languages. "Christmas" and
 * "Holiday" are not: a carol album is as often a pop singer's.
 */
const CHRISTIAN_GENRE =
  /\bchristian\b|\bgospel\b|\bworship\b|\bpraise\b|\bccm\b|\binspirational\b|christlich|chr[ée]tien|cristian|louange|alabanza|adoraci[óo]n|lovsang|lofsöng/i

export function isChristianGenre(genre: string): boolean {
  return CHRISTIAN_GENRE.test(genre)
}

/** A recording is Christian or gospel when any of its genres is. */
export function isChristianRecording(genres: readonly string[] | null | undefined): boolean {
  return (genres ?? []).some(isChristianGenre)
}

// ---- names --------------------------------------------------------------------------------

/**
 * The first artist Apple names: "Elevation Worship & Chandler Moore" →
 * "Elevation Worship"; "Maverick City Music, Chandler Moore & Cody Carnes" →
 * "Maverick City Music"; "Hillsong UNITED feat. TAYA" → "Hillsong UNITED".
 */
export function primaryArtist(artistName: string): string {
  const [first] = artistName.split(/\s*(?:,|&|\bfeat\.?|\bft\.|\bwith\b|\bx\b|\/)\s*/i)
  return (first ?? artistName).trim() || artistName.trim()
}

/**
 * What a version adds to a title, in brackets or after " - ": "(Live)",
 * "(Live at Gateway Conference)", "(feat. Chris Brown)", "- Single Version",
 * "(Radio Version)", "[Radio Edit]", "- Remastered 2011", "(Acoustic)".
 * Not the title's own words in brackets: "(My Chains Are Gone)", "(Where
 * Feet May Fail)", "(He Won't)", "(Bless the Lord)" stay.
 */
const VERSION_TAG =
  /^(?:live\b.*|.*\blive\s*$|(?:feat|ft)\.?\s.+|featuring\s.+|.*\bversion|.*\bedit|.*\bremaster(?:ed)?\b.*|.*\b(?:re)?mix\b.*|acoustic|unplugged|radio|single|demo|instrumental|studio|official (?:audio|video|lyric video)|lyric video|bonus track|\d{4} remaster)$/i

function isVersionTag(text: string) {
  return VERSION_TAG.test(text.trim())
}

/**
 * A recording's name as a song's title: what the version adds taken off.
 * "God I'm Just Grateful (Live)" → "God I'm Just Grateful"; "Praise (feat.
 * Brandon Lake, Chris Brown & Chandler Moore)" → "Praise"; "Holy Forever -
 * Single Version" → "Holy Forever"; "Oceans (Where Feet May Fail)" stays.
 */
export function cleanCatalogTitle(name: string): string {
  let title = name.trim()
  for (let pass = 0; pass < 4; pass++) {
    const before = title
    // A bracketed tag at the end: "(Live)", "[Radio Edit]".
    title = title.replace(/\s*[([]([^()[\]]*)[)\]]\s*$/, (whole, inner: string) =>
      isVersionTag(inner) ? '' : whole,
    )
    // A " - " tail: "- Single Version", "- Live at …".
    title = title.replace(/\s+[-–—]\s+([^-–—]+)$/, (whole, tail: string) =>
      isVersionTag(tail) ? '' : whole,
    )
    // "feat." with no brackets: "Praise feat. Brandon Lake".
    title = title.replace(/\s+(?:feat|ft)\.\s.*$/i, '')
    title = title.trim()
    if (title === before) break
  }
  return title || name.trim()
}

// ---- ranking what someone types ------------------------------------------------------------

/** One recording offered while typing: the catalog's, with what the ranking needs. */
export interface RankableRecording {
  recording: Pick<CatalogSong, 'id' | 'name' | 'artistName' | 'albumName'>
  /** Christian or gospel (`isChristianRecording` of its genres). */
  christian: boolean
  /**
   * Churches singing it this month, as shown (`shownChurches`): 0 below the
   * threshold, so the order never tells one church from none.
   */
  churches: number
}

export interface RankOptions {
  /** At most this many results. */
  limit?: number
  /** Of which at most this many are not Christian or gospel; they go last. */
  maxOther?: number
  /** What was typed: a live recording is kept over the studio one when it says "live". */
  query?: string
}

/** A recording's kind, in the order a duplicate is kept: studio first unless asked. */
const KIND_ORDER: RecordingKind[] = [
  'studio',
  'acoustic',
  'live',
  'remix',
  'instrumental',
  'tribute',
  'karaoke',
]

/** Near-duplicates share this: the title as it would be cleaned and the first artist. */
export function duplicateKey(recording: Pick<CatalogSong, 'name' | 'artistName'>): string {
  return `${normaliseCatalogTitle(cleanCatalogTitle(recording.name))}|${normaliseCatalogTitle(
    primaryArtist(recording.artistName),
  )}`
}

/**
 * Suggestions while someone types a song's title, in the order shown:
 *
 * 1. Near-duplicates collapse — the same title (cleaned, normalised) by the
 *    same first artist: the studio recording stays, unless what was typed
 *    says "live" (then the live one). Otherwise the first, in the order given.
 * 2. Christian and gospel recordings first, the ones more churches sing
 *    before the others, then in the order given (Apple's relevance).
 * 3. At most `maxOther` (2) others, last, in the order given — room is kept
 *    for them, so a church that sings a song from outside the genre still
 *    finds it.
 * 4. At most `limit` (10) in all.
 */
export function rankCatalogResults<T extends RankableRecording>(
  results: readonly T[],
  options: RankOptions = {},
): T[] {
  const limit = options.limit ?? 10
  const maxOther = options.maxOther ?? 2
  const wantsLive = options.query ? recordingKind(options.query) === 'live' : false
  const preference = (item: T) => {
    const kind = recordingKind(item.recording.name, item.recording.albumName)
    if (wantsLive && kind === 'live') return -1
    return KIND_ORDER.indexOf(kind)
  }

  // 1. One per near-duplicate group, at the place of the group's first.
  const groups = new Map<string, { at: number; item: T }>()
  results.forEach((item, at) => {
    const key = duplicateKey(item.recording)
    const kept = groups.get(key)
    if (!kept) groups.set(key, { at, item })
    else if (preference(item) < preference(kept.item)) groups.set(key, { at: kept.at, item })
  })
  const unique = [...groups.values()].sort((a, b) => a.at - b.at)

  // 2–4.
  const christian = unique
    .filter(({ item }) => item.christian)
    .sort((a, b) => b.item.churches - a.item.churches || a.at - b.at)
    .map(({ item }) => item)
  const others = unique
    .filter(({ item }) => !item.christian)
    .map(({ item }) => item)
    .slice(0, Math.max(0, Math.min(maxOther, limit)))
  return [...christian.slice(0, Math.max(0, limit - others.length)), ...others]
}

/**
 * When to start suggesting: once a first word is typed — a word of two
 * letters or more followed by a space ("go "), or four letters ("good").
 */
export function typeaheadReady(raw: string): boolean {
  if (/[\p{L}\p{N}]{2,}\s/u.test(raw)) return true
  return (raw.match(/[\p{L}\p{N}]/gu) ?? []).length >= 4
}

/** The query as it is sent and cached: trimmed, spaces collapsed, lower case. */
export function typeaheadQuery(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ').toLowerCase()
}

// ---- a new song from a recording --------------------------------------------------------------

/** What choosing a recording fills in on a new song. */
export interface SongPrefill {
  title: string
  /** As Apple writes it ("Elevation Worship & Chandler Moore"): who recorded this version. */
  artist: string | null
  /** The recording's composers. */
  author: string | null
  /** The album's copyright line ("℗ 2025 Elevation Worship Records"), when fetched. */
  copyright: string | null
  /** The recording's length, for the default arrangement. */
  lengthSeconds: number | null
}

const SONG_TEXT_MAX = 200
const clip = (text: string | null | undefined) => {
  const value = text?.trim()
  if (!value) return null
  return value.length <= SONG_TEXT_MAX ? value : `${value.slice(0, SONG_TEXT_MAX - 1).trimEnd()}…`
}

export function songFromRecording(
  recording: Pick<CatalogSong, 'name' | 'artistName' | 'composerName' | 'durationMs'>,
  album: { copyright: string | null } | null = null,
): SongPrefill {
  return {
    title: clip(cleanCatalogTitle(recording.name)) ?? recording.name.slice(0, SONG_TEXT_MAX),
    artist: clip(recording.artistName),
    author: clip(recording.composerName),
    copyright: clip(album?.copyright),
    lengthSeconds: recording.durationMs ? Math.round(recording.durationMs / 1000) : null,
  }
}

// ---- how many churches sing it ------------------------------------------------------------------

/**
 * A count of churches is shown only from this many up (k-anonymity): with
 * fewer, a church could recognise another by what it sings.
 */
export const USAGE_MIN_CHURCHES = 3

/** The count to show, or null when too few churches sing it to say. */
export function shownChurches(churches: number | null | undefined): number | null {
  return churches != null && churches >= USAGE_MIN_CHURCHES ? churches : null
}

/** Churches singing a recording: this Sunday's week, and the four weeks to it. */
export interface CatalogUsage {
  /** Null below the threshold. */
  sunday: number | null
  month: number | null
}

/** Both counts through `shownChurches`; null when neither can be shown. */
export function shownUsage(
  raw: { sunday: number | null; month: number | null } | null | undefined,
): CatalogUsage | null {
  const sunday = shownChurches(raw?.sunday)
  const month = shownChurches(raw?.month)
  return sunday === null && month === null ? null : { sunday, month }
}

const churchWord = (n: number) => (n === 1 ? 'church' : 'churches')

/**
 * "Sung by 12 churches this Sunday · 40 this month", "Sung by 40 churches
 * this month", or null when neither count can be shown.
 */
export function usageLine(usage: CatalogUsage | null | undefined): string | null {
  if (!usage) return null
  const { sunday, month } = usage
  if (sunday !== null && month !== null && month > sunday)
    return `Sung by ${sunday} ${churchWord(sunday)} this Sunday · ${month} this month`
  if (sunday !== null) return `Sung by ${sunday} ${churchWord(sunday)} this Sunday`
  if (month !== null) return `Sung by ${month} ${churchWord(month)} this month`
  return null
}

/** "Used by 12 churches": a suggestion row's short form (this month), or null. */
export function usedByLine(usage: CatalogUsage | null | undefined): string | null {
  const month = usage?.month ?? usage?.sunday ?? null
  return month === null ? null : `Used by ${month} ${churchWord(month)}`
}

// ---- weeks ----------------------------------------------------------------------------------------

const DAY_MS = 86_400_000
const toDay = (date: string) => new Date(`${date}T12:00:00Z`)
const isoDay = (date: Date) => date.toISOString().slice(0, 10)

/** The Sunday a day's week starts on (YYYY-MM-DD → YYYY-MM-DD): the usage's week. */
export function sundayWeekStart(date: string): string {
  const day = toDay(date)
  return isoDay(new Date(day.getTime() - day.getUTCDay() * DAY_MS))
}

/** This Sunday, as a church says it: today when it is Sunday, else the next one. */
export function comingSunday(today: string): string {
  const day = toDay(today)
  const ahead = (7 - day.getUTCDay()) % 7
  return isoDay(new Date(day.getTime() + ahead * DAY_MS))
}

/** `date` moved by `weeks` (negative: back). */
export function addWeeks(date: string, weeks: number): string {
  return isoDay(new Date(toDay(date).getTime() + weeks * 7 * DAY_MS))
}
